import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ActivityTrackingService } from '@core/services/activity-tracking.service';

/**
 * Custom error thrown when a user is redirected due to unauthorized access (401).
 * Calling code can catch this to distinguish logout redirects from other errors.
 */
export class UnauthorizedError extends Error {
  constructor(
    message: string = 'User was redirected due to unauthorized access',
  ) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * HTTP error with status code for API error handling
 */
export class HttpError extends Error {
  constructor(
    public status: number,
    message?: string,
  ) {
    super(message || `HTTP error! status: ${status}`);
    this.name = 'HttpError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.apiBaseUrl;
  private oidcSecurityService = inject(OidcSecurityService);
  private activityTrackingService = inject(ActivityTrackingService);
  private router = inject(Router);
  private isHandlingUnauthorized = false;

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    try {
      const token = await this.oidcSecurityService.getAccessToken().toPromise();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.debug('Could not retrieve access token:', error);
      // Return headers without Authorization - 401s will be handled by handleUnauthorized()
    }

    return headers;
  }

  private handleUnauthorized(): never {
    // Guard against multiple simultaneous 401 responses
    if (!this.isHandlingUnauthorized) {
      this.isHandlingUnauthorized = true;

      // Store access-denied message to show after logout redirect
      sessionStorage.setItem('postLogoutMessage', 'access-denied');

      // Perform full logout to clear identity server session
      // This redirects to identity server logout, then back to postLogoutRedirectUri (/login)
      this.oidcSecurityService.logoff('portal-auth-config').subscribe({
        error: () => {
          // If logout fails, still try to redirect
          this.router
            .navigate(['/login'], {
              queryParams: { message: 'access-denied' },
            })
            .finally(() => {
              this.isHandlingUnauthorized = false;
            });
        },
      });
    }

    // Throw custom error to inform calling code that user was redirected
    throw new UnauthorizedError();
  }

  private handleNetworkError(error: unknown): never {
    // Network errors (connection refused, no internet, etc.) throw TypeError from fetch
    if (error instanceof TypeError) {
      throw new HttpError(
        0,
        'Unable to connect to server. Please check that the server is running and try again.',
      );
    }
    // Re-throw if it's not a network error
    throw error;
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    if (response.status === 401) {
      this.handleUnauthorized(); // Throws UnauthorizedError
    }
    if (response.status === 503) {
      throw new HttpError(
        503,
        'Server is temporarily unavailable. Please try again in a few moments.',
      );
    }
    // Try to parse error message from response body
    let errorMessage: string | undefined;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorBody = await response.json();
        errorMessage =
          errorBody.message || errorBody.error || `HTTP ${response.status}`;
      }
    } catch (e) {
      // If parsing fails, will use default message
    }
    throw new HttpError(response.status, errorMessage);
  }

  async get<T>(
    endpoint: string,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}${endpoint}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: 'GET',
        headers,
        signal: options?.signal,
      });
    } catch (error) {
      this.handleNetworkError(error);
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    this.activityTrackingService.trackActivity(url);

    // Handle 204 No Content or empty 200 responses (e.g., when backend returns null)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // If it's a 200 with no content-type, treat as empty/undefined
      // This happens when NestJS returns null
      if (response.status === 200) {
        return undefined as T;
      }
      throw new Error(
        `Unexpected non-JSON response: ${response.status} ${contentType || 'no content-type'}`,
      );
    }

    return response.json();
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: {
      onUploadProgress?: (progress: { loaded: number; total?: number }) => void;
    },
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // For FormData uploads, use XMLHttpRequest to support progress tracking
    if (body instanceof FormData && options?.onUploadProgress) {
      const result = await this.postWithProgress<T>(
        endpoint,
        body,
        options.onUploadProgress,
      );
      this.activityTrackingService.trackActivity(url);
      return result;
    }

    const headers = await this.getHeaders();
    let response: Response;

    // For FormData, don't set Content-Type header (browser sets it with boundary)
    // and don't stringify the body
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
    };

    if (body instanceof FormData) {
      // Remove Content-Type for FormData - browser will set multipart/form-data with boundary
      delete (fetchOptions.headers as Record<string, string>)['Content-Type'];
      fetchOptions.body = body;
    } else if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      response = await fetch(url, fetchOptions);
    } catch (error) {
      this.handleNetworkError(error);
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    this.activityTrackingService.trackActivity(url);

    // Handle 204 No Content or empty 200 responses (e.g., when backend returns null)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // If it's a 200 with no content-type, treat as empty/undefined
      // This happens when NestJS returns null
      if (response.status === 200) {
        return undefined as T;
      }
      throw new Error(
        `Unexpected non-JSON response: ${response.status} ${contentType || 'no content-type'}`,
      );
    }

    return response.json();
  }

  private async postWithProgress<T>(
    endpoint: string,
    formData: FormData,
    onProgress: (progress: { loaded: number; total?: number }) => void,
  ): Promise<T> {
    let token: string | null = null;
    try {
      const accessToken = await this.oidcSecurityService
        .getAccessToken()
        .toPromise();
      token = (accessToken as string | null | undefined) ?? null;
    } catch (error) {
      console.debug('Could not retrieve access token for upload:', error);
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress({ loaded: event.loaded, total: event.total });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            if (xhr.responseText) {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } else {
              resolve(undefined as T);
            }
          } catch (error) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          if (xhr.status === 401) {
            try {
              this.handleUnauthorized(); // Throws UnauthorizedError
            } catch (error) {
              reject(error);
              return;
            }
          }
          if (xhr.status === 503) {
            reject(
              new HttpError(
                503,
                'Server is temporarily unavailable. Please try again in a few moments.',
              ),
            );
            return;
          }
          if (xhr.status === 0) {
            reject(
              new HttpError(
                0,
                'Unable to connect to server. Please check that the server is running and try again.',
              ),
            );
            return;
          }
          reject(new HttpError(xhr.status));
        }
      });

      xhr.addEventListener('error', () => {
        reject(
          new HttpError(
            0,
            'Unable to connect to server. Please check that the server is running and try again.',
          ),
        );
      });

      xhr.open('POST', `${this.baseUrl}${endpoint}`);

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}${endpoint}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: 'PUT',
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (error) {
      this.handleNetworkError(error);
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    this.activityTrackingService.trackActivity(url);

    // Handle 204 No Content or empty 200 responses (e.g., when backend returns null)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // If it's a 200 with no content-type, treat as empty/undefined
      // This happens when NestJS returns null
      if (response.status === 200) {
        return undefined as T;
      }
      throw new Error(
        `Unexpected non-JSON response: ${response.status} ${contentType || 'no content-type'}`,
      );
    }

    return response.json();
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}${endpoint}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (error) {
      this.handleNetworkError(error);
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    this.activityTrackingService.trackActivity(url);

    // Handle 204 No Content or empty 200 responses (e.g., when backend returns null)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // If it's a 200 with no content-type, treat as empty/undefined
      // This happens when NestJS returns null
      if (response.status === 200) {
        return undefined as T;
      }
      throw new Error(
        `Unexpected non-JSON response: ${response.status} ${contentType || 'no content-type'}`,
      );
    }

    return response.json();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}${endpoint}`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
    } catch (error) {
      this.handleNetworkError(error);
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    this.activityTrackingService.trackActivity(url);

    // Handle 204 No Content or empty 200 responses (e.g., when backend returns null)
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // If it's a 200 with no content-type, treat as empty/undefined
      // This happens when NestJS returns null
      if (response.status === 200) {
        return undefined as T;
      }
      throw new Error(
        `Unexpected non-JSON response: ${response.status} ${contentType || 'no content-type'}`,
      );
    }

    return response.json();
  }
}
